import { useEffect, useRef, useState } from 'react';

interface TurnstileProps {
  siteKey: string;
  onVerify: (token: string) => void;
  mode?: string;
}

declare global {
  interface Window {
    turnstile: any;
    onloadTurnstileCallback?: () => void;
  }
}

export function Turnstile({ siteKey, onVerify, mode }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let scriptLoaded = false;

    const loadTurnstile = () => {
      if (window.turnstile && containerRef.current && !widgetIdRef.current) {
        try {
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            callback: (token: string) => {
              console.log('Turnstile verification successful');
              setIsLoading(false);
              onVerify(token);
            },
            'refresh-expired': 'auto',
            'error-callback': (error: string) => {
              console.error('Turnstile encountered an error:', error);
              if (error.includes('domain')) {
                console.error('Please ensure your domain is allowlisted in Cloudflare Turnstile settings');
              }
            },
            theme: 'light',
          });
        } catch (error) {
          console.error('Error rendering Turnstile:', error);
        }
      }
    };

    if (!document.querySelector('script#turnstile-script')) {
      const script = document.createElement('script');
      script.id = 'turnstile-script';
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback';
      script.async = true;
      script.defer = true;

      window.onloadTurnstileCallback = () => {
        scriptLoaded = true;
        loadTurnstile();
      };

      document.head.appendChild(script);
    } else if (window.turnstile) {
      loadTurnstile();
    }

    const intervalId = setInterval(() => {
      if (window.turnstile && !scriptLoaded) {
        loadTurnstile();
        scriptLoaded = true;
      }
    }, 1000);

    return () => {
      if (widgetIdRef.current) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (error) {
          console.error('Error removing Turnstile widget:', error);
        }
      }
      clearInterval(intervalId);
    };
  }, [siteKey, onVerify]);

  // Reset the widget when mode changes
  useEffect(() => {
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch (error) {
        // Widget may not exist yet during StrictMode double-invoke
      }
    }
  }, [mode]);

  return (
    <div className="flex flex-col items-center gap-2">
      {isLoading && <div className="text-sm text-gray-500">Loading verification...</div>}
      <div ref={containerRef} className="flex justify-center" />
    </div>
  );
}
